import FeatherIcon from "feather-icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Modal,
  Col,
  Row,
  Spinner,
  Input,
} from "reactstrap";
import api from "../helpers/api";
import { formatCreatedDate } from "../constants/utils";
import AssetTypeModal from "../components/AssetTypeModal";
import PermissionGuard from "../components/Common/PermissionGuard";
import usePermission from "../Hooks/usePermission";
import { useTranslation } from "react-i18next";

const AssetTypes = () => {
  const { t } = useTranslation();

  const currentUser = JSON.parse(localStorage.getItem('authUser'));
  const organizationId = currentUser?.activeOrganizationId ?? null;
  const { hasPermission } = usePermission();

  const [assetTypeModal, setAssetTypeModal] = useState(false);
  const [selectedAssetType, setSelectedAssetType] = useState(null);
  const [assetTypes, setAssetTypes] = useState([]);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [assetTypeDetailsModal, setAssetTypeDetailsModal] = useState(false);
  const [selectedAssetTypeForDetails, setSelectedAssetTypeForDetails] = useState(null);

  // Toggle asset type modal for create/edit
  function toggleAssetTypeModal(assetType = null) {
    setAssetTypeModal(!assetTypeModal);
    setSelectedAssetType(assetType || {});
    setErrors({});
    setApiError("");
  }

  // Toggle asset type details modal
  const toggleAssetTypeDetailsModal = (assetType = null) => {
    setAssetTypeDetailsModal(!assetTypeDetailsModal);
    setSelectedAssetTypeForDetails(assetType);
  };

  const handleAssetTypeModalClose = () => {
    setAssetTypeModal(false);
    setSelectedAssetType(null);
  };

  const handleAssetTypeUpdate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setApiError("");
    setErrors({});

    // Validation
    const newErrors = {};
    if (!selectedAssetType?.name?.trim()) {
      newErrors.name = t('shared.asset_type_name_required', 'Asset type name is required');
    } else if (selectedAssetType.name.length > 100) {
      newErrors.name = t('shared.asset_type_name_max_length', 'Asset type name must be 100 characters or less');
    }
    if (selectedAssetType?.description && selectedAssetType.description.length > 500) {
      newErrors.description = t('shared.description_max_length', 'Description must be 500 characters or less');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const assetTypeData = {
        name: selectedAssetType.name.trim(),
        description: selectedAssetType.description?.trim() || "",
        organizationId: organizationId,
      };

      if (selectedAssetType?.id) {
        // Update existing asset type
        const updateData = {
          id: selectedAssetType.id,
          ...assetTypeData
        };
        await api.put("organization-asset-type/update", updateData);
      } else {
        // Create new asset type
        await api.post("organization-asset-type/create", assetTypeData);
      }

      handleAssetTypeModalClose();
      reloadAssetTypes(searchTerm);
    } catch (error) {
      console.error("Error during asset type submission:", error);
      setApiError(error?.response?.data?.message || t('shared.error_submission', 'An error occurred during submission'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedAssetType?.id) {
        await api.delete(`organization-asset-type/delete?id=${selectedAssetType.id}`);
        reloadAssetTypes(searchTerm);
        setDeleteModal(false);
      }
    } catch (error) {
      console.error("Error deleting asset type:", error);
      setApiError(error?.response?.data?.message || t('shared.failed_to_delete_asset_type', 'Failed to delete asset type'));
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
  };

  const reloadAssetTypes = useCallback(async (search = "") => {
    if (!organizationId) return;

    setLoading(true);
    try {
      const { data } = await api.get(
        `organization-asset-type/getAll?organizationId=${organizationId}&search=${search}`
      );
      setAssetTypes(data.response || []);
    } catch (error) {
      console.error("Error reloading asset types:", error);
      setApiError(t('shared.failed_to_load_asset_types', 'Failed to load asset types'));
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  // Initial load
  useEffect(() => {
    reloadAssetTypes(searchTerm);
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      reloadAssetTypes(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, reloadAssetTypes]);

  return (
    <div className="mt-4">
      <div className="mb-4 d-flex align-items-center gap-3">
        <motion.div
          className="mb-4 d-flex align-items-center gap-3"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <PermissionGuard permission="asset-type:create">
            <button
              type="button"
              className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2 transition-all hover-shake"
              onClick={() => {
                toggleAssetTypeModal();
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                height={21}
                width={21}
              >
                <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
              </svg>
              {t('shared.add_asset_type', 'Add Asset Type')}
            </button>
          </PermissionGuard>
          <div
            className="ms-auto"
            style={{ minWidth: "400px", flexGrow: 1, maxWidth: "500px" }}
          >
            <Input
              type="text"
              placeholder={t('shared.search_asset_types', 'Search Asset Types...')}
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </motion.div>
      </div>

      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="card-title text-success">{t('shared.asset_types', 'Asset Types')}</h3>
        </CardHeader>
        <CardBody>
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('shared.name', 'Name')}</th>
                  <th>{t('shared.created_date', 'Created Date')}</th>
                  <th>{t('shared.edit', 'Edit')}</th>
                  <th>{t('shared.delete', 'Delete')}</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {assetTypes?.map((assetType, ind) => (
                    <motion.tr
                      key={assetType.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <th scope="row">{ind + 1}</th>
                      <td>
                        <span
                          className="text-primary cursor-pointer hover-underline"
                          onClick={() => toggleAssetTypeDetailsModal(assetType)}
                          style={{ cursor: 'pointer' }}
                        >
                          {assetType.name}
                        </span>
                      </td>
                      <td>{formatCreatedDate(assetType.createdAt) || '-'}</td>
                      <td>
                        <PermissionGuard permission="asset-type:update">
                          <span
                            className='text-warning'
                            role='button'
                            onClick={() => toggleAssetTypeModal(assetType)}
                          >
                            <FeatherIcon icon="edit" />
                          </span>
                        </PermissionGuard>
                      </td>
                      <td>
                        <PermissionGuard permission="asset-type:delete">
                          <span
                            className='text-danger'
                            role='button'
                            onClick={() => {
                              setSelectedAssetType(assetType);
                              setDeleteModal(true);
                            }}
                          >
                            <FeatherIcon icon="trash-2" />
                          </span>
                        </PermissionGuard>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {loading && (
            <div className="text-center py-3">
              <Spinner color="primary" />
            </div>
          )}

          {assetTypes.length === 0 && !loading && (
            <div className="text-center py-5 text-muted">
              {searchTerm
                ? t('shared.no_asset_types_match', 'No asset types match your search')
                : t('shared.no_asset_types_found', 'No asset types found')}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Asset Type Create/Edit Modal */}
      <Modal
        size="lg"
        isOpen={assetTypeModal}
        toggle={() => {
          toggleAssetTypeModal();
        }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header">
            <h5 className="modal-title" id="myLargeModalLabel">
              {selectedAssetType?.id ? t('shared.edit_asset_type', 'Edit Asset Type') : t('shared.add_asset_type', 'Add Asset Type')}
            </h5>
            <button
              onClick={handleAssetTypeModalClose}
              type="button"
              className="btn-close"
              data-dismiss="modal"
              aria-label={t("shared.close", "Close")}
            ></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleAssetTypeUpdate}>
              <Row className="mb-4">
                <label
                  htmlFor="horizontal-name-input"
                  className="col-sm-3 col-form-label"
                >
                  {t('shared.name', 'Name')} <span className="text-danger">*</span>
                </label>
                <Col sm={9}>
                  <input
                    type="text"
                    value={selectedAssetType?.name || ""}
                    onChange={(e) =>
                      setSelectedAssetType({
                        ...selectedAssetType,
                        name: e.target.value,
                      })
                    }
                    className={`form-control ${
                      errors.name ? "is-invalid" : ""
                    }`}
                    placeholder={t('shared.enter_asset_type_name', 'Enter asset type name')}
                    id="horizontal-name-input"
                    maxLength={100}
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name}</small>
                  )}
                </Col>
              </Row>

              <Row className="mb-4">
                <label
                  htmlFor="horizontal-description-input"
                  className="col-sm-3 col-form-label"
                >
                  {t('shared.description', 'Description')}
                </label>
                <Col sm={9}>
                  <textarea
                    value={selectedAssetType?.description || ""}
                    onChange={(e) =>
                      setSelectedAssetType({
                        ...selectedAssetType,
                        description: e.target.value,
                      })
                    }
                    className={`form-control ${
                      errors.description ? "is-invalid" : ""
                    }`}
                    placeholder={t('shared.enter_asset_type_description', 'Enter asset type description')}
                    id="horizontal-description-input"
                    rows={3}
                    maxLength={500}
                  />
                  {errors.description && (
                    <small className="text-danger">{errors.description}</small>
                  )}
                  <small className="text-muted">
                    {selectedAssetType?.description?.length || 0}/500 {t('shared.characters', 'characters')}
                  </small>
                </Col>
              </Row>

              <div className="mt-4">
                {apiError && (
                  <div className="alert alert-danger">{apiError}</div>
                )}
                <button
                  type="submit"
                  className="btn btn-success w-lg transition-all hover-grow pulse-on-hover"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? t('shared.processing', 'Processing...')
                    : selectedAssetType?.id
                    ? t('shared.update', 'Update')
                    : t('shared.add', 'Add')}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <div className="modal-header">
          <h5 className="modal-title">{t('shared.confirm_delete', 'Confirm Delete')}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setDeleteModal(false)}
            aria-label={t("shared.close", "Close")}
          ></button>
        </div>
        <div className="modal-body">
          <p>
            {t('shared.delete_asset_type_confirmation', 'Are you sure you want to delete the asset type "{{name}}"? This action cannot be undone.', { name: selectedAssetType?.name })}
          </p>
          {apiError && (
            <div className="alert alert-danger">{apiError}</div>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setDeleteModal(false)}
          >
            {t('shared.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDelete}
          >
            {t('shared.delete', 'Delete')}
          </button>
        </div>
      </Modal>

      {/* Asset Type Details Modal */}
      <AssetTypeModal
        isOpen={assetTypeDetailsModal}
        toggle={toggleAssetTypeDetailsModal}
        assetTypeData={selectedAssetTypeForDetails}
        loading={false}
        onEditAssetType={hasPermission('asset-type:update') ? (assetType) => {
          toggleAssetTypeDetailsModal();
          toggleAssetTypeModal(assetType);
        } : null}
      />
    </div>
  );
};

export default AssetTypes;
